import React, { useState, useCallback, ChangeEvent } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { useSortable, SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { CustomTextField } from '../TextInput/CustomTextField';

export interface IDraggableFieldList {
    items: string[];
    onItemsChange: (items: string[]) => void;
    placeholder?: string;
    addButtonText?: string;
    maxItems?: number;
    disabled?: boolean;
}

const DraggableFieldItem = ({
    item,
    index,
    themeStyle,
    onRemove,
    disabled,
}: {
    item: string;
    index: number;
    themeStyle: 'light' | 'dark';
    onRemove: () => void;
    disabled?: boolean;
}) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: item });

    return (
        <Box
            ref={setNodeRef}
            sx={{
                transform: CSS.Transform.toString(transform),
                transition,
                display: 'flex',
                alignItems: 'center',
                mb: 1,
                p: 1.5,
                backgroundColor: theme[themeStyle].gray_text,
                borderRadius: '8px',
                border: `1px solid ${theme[themeStyle].placeholderGray50}`,
                '&:hover': { backgroundColor: theme[themeStyle].form_black },
            }}>
            <Box
                {...listeners}
                {...attributes}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mr: 1,
                    cursor: disabled ? 'default' : 'grab',
                    color: theme[themeStyle].tag_white,
                    '&:active': { cursor: disabled ? 'default' : 'grabbing' },
                    opacity: disabled ? 0.6 : 1,
                }}>
                <DragIndicatorIcon />
            </Box>
            <Typography
                sx={{
                    flex: 1,
                    color: theme[themeStyle].tag_white,
                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                }}>
                {item}
            </Typography>
            {!disabled && (
                <IconButton
                    onClick={onRemove}
                    size="small"
                    sx={{
                        color: theme[themeStyle].tag_white,
                        '&:hover': { color: theme[themeStyle].highlight },
                    }}>
                    <CloseIcon />
                </IconButton>
            )}
        </Box>
    );
};

const DraggableFieldList: React.FC<IDraggableFieldList> = ({
    items,
    onItemsChange,
    placeholder = 'Add new item...',
    addButtonText = 'Add',
    maxItems,
    disabled = false,
}) => {
    const { themeStyle } = useThemeContext();
    const sensors = useSensors(useSensor(PointerSensor));
    const [newItem, setNewItem] = useState('');

    const handleAddItem = useCallback(() => {
        if (newItem.trim() && !disabled) {
            if (maxItems && items.length >= maxItems) {
                return; // Don't add if max items reached
            }
            const updatedItems = [...items, newItem.trim()];
            onItemsChange(updatedItems);
            setNewItem('');
        }
    }, [newItem, items, onItemsChange, maxItems, disabled]);

    const handleRemoveItem = useCallback(
        (itemToRemove: string) => {
            if (disabled) return;
            const updatedItems = items.filter(item => item !== itemToRemove);
            onItemsChange(updatedItems);
        },
        [items, onItemsChange, disabled],
    );

    const handleDragEnd = useCallback(
        (event: any) => {
            if (disabled) return;
            const { active, over } = event;
            if (active.id !== over?.id) {
                const oldIndex = items.indexOf(active.id as string);
                const newIndex = items.indexOf(over?.id as string);
                const newItems = arrayMove(items, oldIndex, newIndex);
                onItemsChange(newItems);
            }
        },
        [items, onItemsChange, disabled],
    );

    const canAddMore = !maxItems || items.length < maxItems;

    return (
        <Box sx={{ width: '100%' }}>
            {/* Draggable Items List */}
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={items} strategy={verticalListSortingStrategy}>
                    {items.map((item, index) => (
                        <DraggableFieldItem
                            key={item}
                            item={item}
                            index={index}
                            themeStyle={themeStyle}
                            onRemove={() => handleRemoveItem(item)}
                            disabled={disabled}
                        />
                    ))}
                </SortableContext>
            </DndContext>

            {/* Add New Item Section */}
            {!disabled && canAddMore && (
                <Box sx={{ display: 'flex', mt: 2 }}>
                    <CustomTextField
                        placeholder={placeholder}
                        textFieldProps={{
                            InputProps: {
                                disableUnderline: true,
                                value: newItem,
                                onChange: (e: ChangeEvent<HTMLInputElement>) => setNewItem(e.target.value),
                                style: {
                                    borderRadius: '8px',
                                    color: theme[themeStyle].lightWhite,
                                },
                                onKeyPress: (e: React.KeyboardEvent) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleAddItem();
                                    }
                                },
                            },
                        }}
                        mainSx={{
                            borderWidth: 2,
                        }}
                    />
                    <IconButton
                        onClick={handleAddItem}
                        disabled={!newItem.trim()}
                        sx={{
                            ml: 1,
                            color: theme[themeStyle].tag_white,
                            '&:hover': {
                                color: theme[themeStyle].highlight,
                            },
                            '&:disabled': {
                                color: theme[themeStyle].form_label,
                            },
                        }}>
                        <AddIcon />
                    </IconButton>
                </Box>
            )}

            {/* Max Items Warning */}
            {maxItems && items.length >= maxItems && (
                <Typography
                    sx={{
                        mt: 1,
                        color: theme[themeStyle].form_label,
                        fontSize: '0.75rem',
                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                    }}>
                    Maximum {maxItems} items allowed
                </Typography>
            )}
        </Box>
    );
};

export { DraggableFieldList };
